import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getErrorHttpMessage } from "utils";
import { StoreType } from "types/genericTypes";
import { useIntl } from "react-intl";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  activarCategoriaHttp,
  createCategoriaHttp,
  desactivarCategoriaHttp,
  editarCategoriaHttp,
  getCategoriasHttp,
} from "actions/categorias";
import {
  activarTipoProductoHttp,
  createTipoProductoHttp,
  desactivarTipoProductoHttp,
  editarTipoProductoHttp,
  getTipoProductosHttp,
} from "actions/tipoProducto";

export const useTipoProducto = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const [procesando, setProcesando] = useState<boolean>(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [mensajeAlert, setMensajeAlert] = useState("");
  const [errorLogin, setErrorLogin] = useState(false);
  const handleisAlertOpen = () => setIsAlertOpen(true);
  const handleisAlertClose = () => setIsAlertOpen(false);

  /* CREAR CATEGORÍA */
  const [procesandoTipoProducto, setProcesandoTipoProducto] = useState<boolean>(false);
  const [isAlertOpenCrearTipoProducto, setIsAlertOpenCrearTipoProducto] = useState(false);
  const handleisAlertCrearTipoProductoOpen = () => setIsAlertOpenCrearTipoProducto(true);
  const handleisAlertCrearTipoProductoClose = () => setIsAlertOpenCrearTipoProducto(false);

  const [verEditarTipoProducto, setVerEditarTipoProducto] = useState<any>(null);
  const [isAlertOpenEditarTipoProducto, setIsAlertOpenEditarTipoProducto] = useState(false);
  const handleisAlertOpenEditarTipoProducto = () => setIsAlertOpenEditarTipoProducto(true);
  const handleisAlertCloseEditarTipoProducto = () => setIsAlertOpenEditarTipoProducto(false);

  const [tiposProducto, setTiposProducto] = useState<any[]>([]);

  // Estados para la paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  //para buscador
  const [buscador, setBuscador] = useState("");
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

  //Debounce para evitar muchas peticiones
  const handleBuscadorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBuscador(value);

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    const timeout = setTimeout(() => {
      getTiposProducto({ search: value });
    }, 500);
    setDebounceTimeout(timeout);
  };

  const formik = useFormik({
    initialValues: {
      clave: "",
      nombre: "",
      descripcion: "",
    },
    validationSchema: Yup.object({
      clave: Yup.string(),
      nombre: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
      descripcion: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
    }),
    onSubmit: async (values) => {
    },
  });

  const isFieldValid = (fieldName: keyof typeof formik.values) => {
    return (
      formik.touched[fieldName] &&
      !formik.errors[fieldName] &&
      formik.values[fieldName] &&
      formik.values[fieldName] !== ""
    );
  };

  const getFieldColor = (fieldName: keyof typeof formik.values) => {
    return isFieldValid(fieldName) ? "#00AB16" : undefined;
  };

  const getTiposProducto = useCallback(
    async (params?: { search?: string; fecha1?: string; fecha2?: string }) => {
      try {
        setProcesando(true);
        const tipoProducto = await getTipoProductosHttp(params?.search);
        setTiposProducto(tipoProducto);
        setProcesando(false);
      } catch (error) {
        setProcesando(false);
        const message = getErrorHttpMessage(error);
        setMensajeAlert(message || intl.formatMessage({ id: "get_elementos_error" }));
        handleisAlertOpen();
      }
    },
    []
  );

  const createTipoProducto = async (datos: any) => {
    try {
      setProcesandoTipoProducto(true);
      const response: any = await createTipoProductoHttp(datos);
      setTiposProducto((prevTipoProductos: any[]) => [
        { ...response, productos: 0 },
        ...prevTipoProductos,
      ]);

      setMensajeAlert(intl.formatMessage({ id: "general_añadido_correctamente" }));
      formik.resetForm();
      handleisAlertCrearTipoProductoClose();
      handleisAlertOpen();
      setProcesandoTipoProducto(false);
    } catch (error) {
      setProcesandoTipoProducto(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "error_añadir_general" }));
      handleisAlertOpen();
    }
  };
  const editarTipoProducto = async (datos: any) => {
    try {
      setProcesandoTipoProducto(true);
      const response: any = await editarTipoProductoHttp(datos);

      setTiposProducto((prevTipoProductos: any[]) =>
        prevTipoProductos.map((c) =>
          c.id === response?.id
            ? {
              ...c,
              clave: response?.clave,
              nombre: response?.nombre,
              descripcion: response?.descripcion,
            }
            : c
        )
      );
      setMensajeAlert(intl.formatMessage({ id: "general_editado_correctamente" }));
      formik.resetForm();
      handleisAlertCloseEditarTipoProducto();
      handleisAlertOpen();
      setProcesandoTipoProducto(false);
    } catch (error) {
      setProcesandoTipoProducto(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_editado_error" }));
      handleisAlertOpen();
    }
  };

  const desactivarTipoProducto = async (datos: any) => {
    try {
      setProcesando(true);
      const tipoProducto: any = await desactivarTipoProductoHttp(datos);
      setTiposProducto((prevTipoProductos: any[]) => {
        return prevTipoProductos.map((c) => {
          if (c.id === tipoProducto.id) {
            return {
              ...c,
              estatus: tipoProducto.estatus,
            };
          }
          return c;
        });
      });
      setMensajeAlert(intl.formatMessage({ id: "general_desactivado_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_desactivado_error" }));
      handleisAlertOpen();
    }
  };
  const activarTipoProducto = async (datos: any) => {
    try {
      setProcesando(true);
      const tipoProducto: any = await activarTipoProductoHttp(datos);
      setTiposProducto((prevTipoProductos: any[]) => {
        return prevTipoProductos.map((t) => {
          if (t.id === tipoProducto.id) {
            return {
              ...t,
              estatus: tipoProducto.estatus,
            };
          }
          return t;
        });
      });
      setMensajeAlert(intl.formatMessage({ id: "general_activado_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_activado_error" }));
      handleisAlertOpen();
    }
  };

  useEffect(() => {
    if (verEditarTipoProducto) {
      formik.setValues({
        clave: verEditarTipoProducto?.clave || "",
        nombre: verEditarTipoProducto?.nombre || "",
        descripcion: verEditarTipoProducto?.descripcion || "",
      });
    }
  }, [verEditarTipoProducto]);

  useEffect(() => {
    getTiposProducto();
  }, []);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const tipoProductosPaginados = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return tiposProducto?.slice(startIndex, endIndex) || [];
  }, [tiposProducto, page, rowsPerPage]);

  return {
    intl,
    formik,
    procesando,
    isAlertOpen,
    mensajeAlert,
    handleisAlertClose,
    tiposProducto,
    //crear categoria
    isAlertOpenCrearTipoProducto,
    handleisAlertCrearTipoProductoOpen,
    handleisAlertCrearTipoProductoClose,
    getFieldColor,
    procesandoTipoProducto,
    createTipoProducto,
    desactivarTipoProducto,
    activarTipoProducto,
    editarTipoProducto,
    verEditarTipoProducto,
    isAlertOpenEditarTipoProducto,
    handleisAlertOpenEditarTipoProducto,
    handleisAlertCloseEditarTipoProducto,
    setVerEditarTipoProducto,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    buscador,
    handleBuscadorChange,
    tipoProductosPaginados,
  };
};
